import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  Ban,
  Check,
  Loader2,
  MailPlus,
  ShieldCheck,
  UserCheck,
  UserX,
} from "lucide-react";
import { APP_NAME } from "@crewledger/shared/constants";
import {
  BETA_ACCESS_REQUEST_STATUSES,
  BETA_ACCESS_TYPES,
  BETA_ALLOWLIST_STATUSES,
  type BetaAccessRequest,
  type BetaAccessRequestStatus,
  type BetaAccessType,
  type BetaAllowlistEntry,
  type BetaAllowlistStatus,
} from "@crewledger/shared/beta-access";
import {
  ConfirmationModal,
  EmptyState,
  InfoBanner,
  ListTableSkeleton,
  PageHeader,
  PaginationBar,
  useToast,
} from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/lib/utils";
import {
  addBetaAllowlistEntry,
  approveBetaAccessRequest,
  blockBetaAllowlistEmail,
  fetchBetaAccessAdminAccess,
  fetchBetaAccessRequests,
  fetchBetaAllowlist,
  rejectBetaAccessRequest,
  updateBetaAllowlistEntry,
} from "./api";

const PAGE_SIZE = 25;

type Tab = "requests" | "allowlist";

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function statusClass(status: string): string {
  switch (status) {
    case "NEW":
    case "INVITED":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "REVIEWED":
      return "border-amber-200 bg-amber-50 text-amber-800";
    case "APPROVED":
    case "ACTIVE":
      return "border-green-200 bg-green-50 text-green-700";
    case "REJECTED":
    case "BLOCKED":
      return "border-red-200 bg-red-50 text-red-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

function StatusPill({ value }: { value: string }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold",
        statusClass(value),
      )}
    >
      {value}
    </span>
  );
}

export function BetaAccessAdminPage() {
  const { toast } = useToast();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [tab, setTab] = useState<Tab>("requests");

  const [requests, setRequests] = useState<BetaAccessRequest[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsPage, setRequestsPage] = useState(1);
  const [requestStatus, setRequestStatus] = useState<BetaAccessRequestStatus | "">("");
  const [requestQ, setRequestQ] = useState("");
  const debouncedRequestQ = useDebouncedValue(requestQ, 300);
  const [requestsLoading, setRequestsLoading] = useState(true);

  const [allowlist, setAllowlist] = useState<BetaAllowlistEntry[]>([]);
  const [allowlistTotal, setAllowlistTotal] = useState(0);
  const [allowlistPage, setAllowlistPage] = useState(1);
  const [allowlistStatus, setAllowlistStatus] = useState<BetaAllowlistStatus | "">("");
  const [allowlistAccessType, setAllowlistAccessType] = useState<BetaAccessType | "">("");
  const [allowlistQ, setAllowlistQ] = useState("");
  const debouncedAllowlistQ = useDebouncedValue(allowlistQ, 300);
  const [allowlistLoading, setAllowlistLoading] = useState(true);

  const [actionId, setActionId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<BetaAccessRequest | null>(null);
  const [blockTarget, setBlockTarget] = useState<BetaAllowlistEntry | null>(null);

  const [addEmail, setAddEmail] = useState("");
  const [addCompany, setAddCompany] = useState("");
  const [addAccessType, setAddAccessType] = useState<BetaAccessType>("BETA");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    document.title = `Beta access — ${APP_NAME}`;
  }, []);

  useEffect(() => {
    void fetchBetaAccessAdminAccess()
      .then((res) => { setAllowed(res.data.allowed); })
      .catch(() => { setAllowed(false); });
  }, []);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const res = await fetchBetaAccessRequests({
        page: requestsPage,
        pageSize: PAGE_SIZE,
        status: requestStatus || undefined,
        q: debouncedRequestQ || undefined,
      });
      setRequests(res.data);
      setRequestsTotal(res.meta.total ?? res.data.length);
    } catch (err: unknown) {
      toast({
        title: "Failed to load requests",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, [requestsPage, requestStatus, debouncedRequestQ, toast]);

  const loadAllowlist = useCallback(async () => {
    setAllowlistLoading(true);
    try {
      const res = await fetchBetaAllowlist({
        page: allowlistPage,
        pageSize: PAGE_SIZE,
        status: allowlistStatus || undefined,
        accessType: allowlistAccessType || undefined,
        q: debouncedAllowlistQ || undefined,
      });
      setAllowlist(res.data);
      setAllowlistTotal(res.meta.total ?? res.data.length);
    } catch (err: unknown) {
      toast({
        title: "Failed to load allowlist",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
      setAllowlist([]);
    } finally {
      setAllowlistLoading(false);
    }
  }, [allowlistPage, allowlistStatus, allowlistAccessType, debouncedAllowlistQ, toast]);

  useEffect(() => {
    if (!allowed) return;
    void loadRequests();
  }, [allowed, loadRequests]);

  useEffect(() => {
    if (!allowed) return;
    void loadAllowlist();
  }, [allowed, loadAllowlist]);

  useEffect(() => {
    setRequestsPage(1);
  }, [requestStatus, debouncedRequestQ]);

  useEffect(() => {
    setAllowlistPage(1);
  }, [allowlistStatus, allowlistAccessType, debouncedAllowlistQ]);

  if (allowed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to="/access-denied" replace />;
  }

  const handleApprove = async (request: BetaAccessRequest) => {
    setActionId(request.id);
    try {
      await approveBetaAccessRequest(request.id, {
        accessType: "BETA",
        companyName: request.companyName,
      });
      toast({ variant: "success", title: "Request approved", description: `${request.email} invited to beta.` });
      await Promise.all([loadRequests(), loadAllowlist()]);
    } catch (err: unknown) {
      toast({
        title: "Approve failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionId(rejectTarget.id);
    try {
      await rejectBetaAccessRequest(rejectTarget.id);
      toast({ variant: "success", title: "Request rejected", description: rejectTarget.email });
      setRejectTarget(null);
      await loadRequests();
    } catch (err: unknown) {
      toast({
        title: "Reject failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    } finally {
      setActionId(null);
    }
  };

  const handleBlock = async () => {
    if (!blockTarget) return;
    setActionId(blockTarget.id);
    try {
      await blockBetaAllowlistEmail(blockTarget.email);
      toast({ variant: "success", title: "Email blocked", description: blockTarget.email });
      setBlockTarget(null);
      await loadAllowlist();
    } catch (err: unknown) {
      toast({
        title: "Block failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    } finally {
      setActionId(null);
    }
  };

  const handleAddAllowlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addEmail.trim()) return;
    setAdding(true);
    try {
      await addBetaAllowlistEntry({
        email: addEmail.trim(),
        companyName: addCompany.trim() || undefined,
        accessType: addAccessType,
        status: "INVITED",
      });
      toast({ variant: "success", title: "Allowlist entry added", description: addEmail.trim() });
      setAddEmail("");
      setAddCompany("");
      setAddAccessType("BETA");
      await loadAllowlist();
    } catch (err: unknown) {
      toast({
        title: "Add failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    } finally {
      setAdding(false);
    }
  };

  const handleAccessTypeChange = async (entry: BetaAllowlistEntry, accessType: BetaAccessType) => {
    if (entry.accessType === accessType) return;
    setActionId(entry.id);
    try {
      await updateBetaAllowlistEntry(entry.email, { accessType });
      toast({ variant: "success", title: "Access type updated", description: `${entry.email} → ${accessType}` });
      await loadAllowlist();
    } catch (err: unknown) {
      toast({
        title: "Update failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "error",
      });
    } finally {
      setActionId(null);
    }
  };

  const pendingRequest = (status: BetaAccessRequestStatus) =>
    status === "NEW" || status === "REVIEWED";

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto max-w-7xl space-y-6 p-6">
        <PageHeader
          title="Beta access management"
          description="Review access requests, manage the allowlist, and control beta invitations."
          badge={
            <span className="inline-flex items-center gap-1.5 rounded-full bg-navy-deep/5 px-2.5 py-0.5 text-xs font-semibold text-navy-blueprint">
              <ShieldCheck className="h-3.5 w-3.5" />
              Platform admin
            </span>
          }
          actions={
            <Link to="/admin">
              <Button variant="outline" size="sm">
                Admin home
              </Button>
            </Link>
          }
        />

        <InfoBanner variant="info">
          Approving a request adds the email to the allowlist with INVITED status and BETA access by
          default. All changes are recorded in the audit log.
        </InfoBanner>

        <div className="flex gap-2 border-b">
          <button
            type="button"
            onClick={() => { setTab("requests"); }}
            className={cn(
              "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              tab === "requests"
                ? "border-navy-deep text-navy-deep"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            Access requests
          </button>
          <button
            type="button"
            onClick={() => { setTab("allowlist"); }}
            className={cn(
              "border-b-2 px-4 py-2 text-sm font-medium transition-colors",
              tab === "allowlist"
                ? "border-navy-deep text-navy-deep"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            Allowlist
          </button>
        </div>

        {tab === "requests" ? (
          <div className="space-y-4">
            <div className="grid gap-3 rounded-xl border bg-card-white p-4 sm:grid-cols-2">
              <Input
                placeholder="Search email or company…"
                value={requestQ}
                onChange={(e) => { setRequestQ(e.target.value); }}
              />
              <select
                value={requestStatus}
                onChange={(e) => { setRequestStatus(e.target.value as BetaAccessRequestStatus | ""); }}
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              >
                <option value="">All statuses</option>
                {BETA_ACCESS_REQUEST_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {requestsLoading ? (
              <ListTableSkeleton rows={6} cols={7} />
            ) : requests.length === 0 ? (
              <EmptyState
                icon={MailPlus}
                title="No access requests"
                description="Requests from the public form will appear here."
              />
            ) : (
              <div className="overflow-hidden rounded-xl border bg-card-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.id}>
                        <TableCell className="font-medium">{request.name}</TableCell>
                        <TableCell>{request.email}</TableCell>
                        <TableCell>{request.companyName}</TableCell>
                        <TableCell className="text-muted-foreground">{request.companySize}</TableCell>
                        <TableCell>
                          <StatusPill value={request.status} />
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(request.createdAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          {pendingRequest(request.status) ? (
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={actionId === request.id}
                                onClick={() => void handleApprove(request)}
                              >
                                {actionId === request.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <>
                                    <UserCheck className="mr-1 h-4 w-4" />
                                    Approve
                                  </>
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={actionId === request.id}
                                onClick={() => { setRejectTarget(request); }}
                              >
                                <UserX className="mr-1 h-4 w-4" />
                                Reject
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <PaginationBar
              page={requestsPage}
              pageSize={PAGE_SIZE}
              total={requestsTotal}
              onPageChange={setRequestsPage}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <form
              onSubmit={(e) => void handleAddAllowlist(e)}
              className="grid gap-4 rounded-xl border bg-card-white p-4 sm:grid-cols-2 lg:grid-cols-5"
            >
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="add-email">Email</Label>
                <Input
                  id="add-email"
                  type="email"
                  placeholder="user@company.com"
                  value={addEmail}
                  onChange={(e) => { setAddEmail(e.target.value); }}
                  required
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="add-company">Company (optional)</Label>
                <Input
                  id="add-company"
                  placeholder="Company name"
                  value={addCompany}
                  onChange={(e) => { setAddCompany(e.target.value); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="add-access-type">Access type</Label>
                <select
                  id="add-access-type"
                  value={addAccessType}
                  onChange={(e) => { setAddAccessType(e.target.value as BetaAccessType); }}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                >
                  {BETA_ACCESS_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end sm:col-span-2 lg:col-span-5">
                <Button type="submit" disabled={adding}>
                  {adding ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <MailPlus className="mr-1.5 h-4 w-4" />
                  )}
                  Add to allowlist
                </Button>
              </div>
            </form>

            <div className="grid gap-3 rounded-xl border bg-card-white p-4 sm:grid-cols-3">
              <Input
                placeholder="Search email or company…"
                value={allowlistQ}
                onChange={(e) => { setAllowlistQ(e.target.value); }}
              />
              <select
                value={allowlistStatus}
                onChange={(e) => { setAllowlistStatus(e.target.value as BetaAllowlistStatus | ""); }}
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              >
                <option value="">All statuses</option>
                {BETA_ALLOWLIST_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <select
                value={allowlistAccessType}
                onChange={(e) => { setAllowlistAccessType(e.target.value as BetaAccessType | ""); }}
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              >
                <option value="">All access types</option>
                {BETA_ACCESS_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {allowlistLoading ? (
              <ListTableSkeleton rows={6} cols={6} />
            ) : allowlist.length === 0 ? (
              <EmptyState
                icon={Check}
                title="No allowlist entries"
                description="Approve requests or add emails manually."
              />
            ) : (
              <div className="overflow-hidden rounded-xl border bg-card-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Access type</TableHead>
                      <TableHead>Used at</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allowlist.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-medium">{entry.email}</TableCell>
                        <TableCell>{entry.companyName ?? "—"}</TableCell>
                        <TableCell>
                          <StatusPill value={entry.status} />
                        </TableCell>
                        <TableCell>
                          <select
                            value={entry.accessType}
                            disabled={actionId === entry.id || entry.status === "BLOCKED"}
                            onChange={(e) =>
                              void handleAccessTypeChange(entry, e.target.value as BetaAccessType)
                            }
                            className="h-8 rounded-md border border-input bg-transparent px-2 text-sm"
                          >
                            {BETA_ACCESS_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(entry.usedAt)}
                        </TableCell>
                        <TableCell className="text-right">
                          {entry.status !== "BLOCKED" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={actionId === entry.id}
                              onClick={() => { setBlockTarget(entry); }}
                            >
                              <Ban className="mr-1 h-4 w-4" />
                              Block
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">Blocked</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <PaginationBar
              page={allowlistPage}
              pageSize={PAGE_SIZE}
              total={allowlistTotal}
              onPageChange={setAllowlistPage}
            />
          </div>
        )}
      </div>

      <ConfirmationModal
        open={rejectTarget !== null}
        onClose={() => { setRejectTarget(null); }}
        onConfirm={() => void handleReject()}
        title="Reject access request"
        description={
          rejectTarget
            ? `Reject the request from ${rejectTarget.email}? They will not be added to the allowlist.`
            : ""
        }
        confirmLabel="Reject"
        variant="destructive"
        loading={rejectTarget !== null && actionId === rejectTarget.id}
      />

      <ConfirmationModal
        open={blockTarget !== null}
        onClose={() => { setBlockTarget(null); }}
        onConfirm={() => void handleBlock()}
        title="Block email"
        description={
          blockTarget
            ? `Block ${blockTarget.email}? They will lose beta access immediately.`
            : ""
        }
        confirmLabel="Block"
        variant="destructive"
        loading={blockTarget !== null && actionId === blockTarget.id}
      />
    </div>
  );
}
